import {jsPDF} from "jspdf";
import notoSansRegularUrl from "../assets/fonts/NotoSans-Regular.ttf";
import notoSansBoldUrl from "../assets/fonts/NotoSans-Bold.ttf";

const safeText = (text) => String(text ?? '');

let cachedFonts = null;

const arrayBufferToBinaryString = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return binary;
};

const loadFonts = async () => {
  if (!cachedFonts) {
    cachedFonts = Promise.all([
      fetch(notoSansRegularUrl).then((response) => response.arrayBuffer()).then(arrayBufferToBinaryString),
      fetch(notoSansBoldUrl).then((response) => response.arrayBuffer()).then(arrayBufferToBinaryString)
    ]).then(([regular, bold]) => ({regular, bold}));
  }
  return cachedFonts;
};

const registerFonts = async (doc) => {
  const fonts = await loadFonts();
  doc.addFileToVFS('NotoSans-Regular.ttf', fonts.regular);
  doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');
  doc.addFileToVFS('NotoSans-Bold.ttf', fonts.bold);
  doc.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold');
};

const drawLabelValue = (doc, xLabel, xValue, yStart, label, value) => {
  const safeLabel = safeText(label);
  const safeValue = safeText(value || '-');
  doc.setFont("NotoSans", "normal");
  doc.text(safeLabel, xLabel, yStart);
  doc.text(safeValue, xValue, yStart);
  return yStart + 20;
};

const sanitizeFileNamePart = (value) => String(value || 'accommodation').trim()
  .replace(/[^\w.-]+/g, '_')
  .replace(/^_+|_+$/g, '');

const localDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const generateAccommodationCertificatePdf = async ({
  accommodationName,
  accommodationAddress,
  ntakServiceId,
  ntakRegistrationNumber,
  operatorCompanyName,
  operatorSeat,
  operatorTaxNumber,
  signImageDataUrl = ''
}) => {
  const doc = new jsPDF({orientation: 'portrait', unit: 'pt', format: 'a4'});
  await registerFonts(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const left = 56;
  const right = pageWidth - 56;
  let y = 72;

  doc.setFont("NotoSans", "bold");
  doc.setFontSize(34 / 2);
  doc.text(safeText('Igazolás'), pageWidth / 2, y, {align: 'center'});
  y += 42;

  doc.setFont("NotoSans", "normal");
  doc.setFontSize(10);
  const p1 = safeText('A 7Hills IT Kft. (Cégjegyzékszám: 13 09 173849, Adószám: 14269550-2-13, Cím: 2013 Pomáz, Kandó Kálmán u. 18. Ügyvezető: Takáts Tamás) képviseletében kijelentem, hogy cégünk üzemelteti a 7H NTAK Connector (FelhőMatrac) szálláshelykezelő szoftvert, mely szoftver a 235/2019. (X. 15) kormányrendelet elvárásainak megfelel.');
  const p2 = safeText('A Magyar Turisztikai Ügynökség (MTÜ) igazolja a szoftver adatküldési megfelelőségét az NTAK felé. Kijelentem, hogy szoftverünk az igazolás kiállításakor érvényes NTAK engedéllyel rendelkezik. Az NTAK igazolás iktatószáma: MTÜ/ALT/2341-46/2019');
  const p3 = safeText('Igazoljuk, hogy fenti szálláshely(ek) üzemeltetője előfizető ügyfele a FelhőMatrac szálláshelykezelőnek, így NTAK adatszolgáltatási kötelezettségének a FelhőMatrac szoftveren keresztül tesz eleget.');
  doc.text(doc.splitTextToSize(p1, right - left), left, y);
  y += 56;
  doc.text(doc.splitTextToSize(p2, right - left), left, y);
  y += 74;

  doc.setFont("NotoSans", "bold");
  doc.text(safeText('Üzemeltető:'), left, y);
  y += 20;
  y = drawLabelValue(doc, left, left + 210, y, 'Cégnév:', operatorCompanyName);
  y = drawLabelValue(doc, left, left + 210, y, 'Székhely:', operatorSeat);
  y = drawLabelValue(doc, left, left + 210, y, 'Adószám:', operatorTaxNumber);
  y += 14;

  doc.setFont("NotoSans", "bold");
  doc.text(safeText('Szálláshely:'), left, y);
  y += 20;
  y = drawLabelValue(doc, left, left + 210, y, 'Megnevezése:', accommodationName);
  y = drawLabelValue(doc, left, left + 210, y, 'Címe:', accommodationAddress);
  y = drawLabelValue(doc, left, left + 210, y, 'NTAK Adatszolgáltatási azonosító:', ntakServiceId);
  y = drawLabelValue(doc, left, left + 210, y, 'NTAK Regisztrációs szám:', ntakRegistrationNumber);
  y += 28;

  doc.setFont("NotoSans", "normal");
  doc.text(doc.splitTextToSize(p3, right - left), left, y);

  const footerY = 760;
  doc.text(safeText(`Budapest, ${localDateString()}`), left, footerY);
  if (signImageDataUrl) {
    const signImageFormat = signImageDataUrl.includes('image/jpeg') ? 'JPEG' : 'PNG';
    doc.addImage(signImageDataUrl, signImageFormat, pageWidth - 210, footerY - 65, 130, 65);
  }
  doc.text(safeText('Takáts Tamás'), pageWidth - 170, footerY + 14);
  doc.text(safeText('Ügyvezető'), pageWidth - 170, footerY + 28);
  doc.text(safeText('7Hills IT Kft.'), pageWidth - 170, footerY + 42);

  const sanitizedName = sanitizeFileNamePart(accommodationName);
  doc.save(`${sanitizedName || 'accommodation'}_ntak_igazolas.pdf`);
};
